/**
 * Responsibility: Define shared admin roles, feature flags, and local runtime persistence helpers.
 * Scope: Shared admin contracts/runtime helpers only; UI orchestration and Firestore access stay elsewhere.
 */
export type AdminProviderOverride =
  | 'auto'
  | 'mock'
  | 'ollama'
  | 'vertex-managed'
  | 'groq'
  | 'openai'
  | 'gemini'
  | 'anthropic'

export type ManagedAccessMode = 'open' | 'entitlements' | 'grants_only'

export interface AdminFeatureFlags {
  maintenanceMode: boolean
  registrationOpen: boolean
  providerOverride: AdminProviderOverride
  managedAccessMode: ManagedAccessMode
  logicQuotaPerHour: number
  imageQuotaPerDay: number
  updatedAtMs?: number
}

export type AdminUserRole = 'normal' | 'creator' | 'moderator' | 'admin' | 'supreme'

export interface AdminRoleGrant {
  uid?: string
  email?: string
  displayName?: string
  role: AdminUserRole
  updatedAtMs?: number
  updatedByUid?: string
  updatedByEmail?: string
}

export const SUPREME_ADMIN_EMAIL = 'dharmax@gmail.com'
export const LOCAL_ADMINS_KEY = 'am2_admin_local_admins_v1'
export const LOCAL_FLAGS_KEY = 'am2_admin_local_flags_v1'
export const LOCAL_ROLE_GRANTS_KEY = 'am2_admin_local_role_grants_v1'
export const E2E_ADMIN_KEY = 'am2_e2e_admin'

export const DEFAULT_ADMIN_FEATURE_FLAGS: AdminFeatureFlags = {
  maintenanceMode: false,
  registrationOpen: true,
  providerOverride: 'auto',
  managedAccessMode: 'entitlements',
  logicQuotaPerHour: 0,
  imageQuotaPerDay: 0,
}

const ALLOWED_PROVIDERS = new Set<AdminProviderOverride>([
  'auto', 'mock', 'ollama', 'vertex-managed', 'groq', 'openai', 'gemini', 'anthropic',
])

const ALLOWED_ACCESS_MODES = new Set<ManagedAccessMode>([
  'open', 'entitlements', 'grants_only',
])

const ROLE_RANK: Record<AdminUserRole, number> = {
  normal: 0,
  creator: 1,
  moderator: 2,
  admin: 3,
  supreme: 4,
}

function safeObject(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' ? raw as Record<string, unknown> : {}
}

export function normalizeAdminEmail(value: unknown): string {
  return String(value ?? '').trim().toLowerCase()
}

function normalizeAdminUid(value: unknown): string {
  return String(value ?? '').trim()
}

export function normalizeAdminRole(value: unknown, email = ''): AdminUserRole {
  if (normalizeAdminEmail(email) === SUPREME_ADMIN_EMAIL) return 'supreme'
  if (value === 'creator' || value === 'moderator' || value === 'admin' || value === 'supreme') {
    return value
  }
  return 'normal'
}

export function roleLabel(role: AdminUserRole): string {
  if (role === 'supreme') return 'Supreme Admin'
  if (role === 'admin') return 'Admin'
  if (role === 'moderator') return 'Moderator'
  if (role === 'creator') return 'Creator'
  return 'Normal'
}

export function isAdminRole(role: AdminUserRole): boolean {
  return role === 'admin' || role === 'supreme'
}

export function isModeratorRole(role: AdminUserRole): boolean {
  return role === 'moderator' || isAdminRole(role)
}

export function normalizeFeatureFlags(raw: unknown): AdminFeatureFlags {
  const entry = safeObject(raw)
  const logicQuota = Number(entry.logicQuotaPerHour)
  const imageQuota = Number(entry.imageQuotaPerDay)
  const providerOverride = String(entry.providerOverride ?? 'auto') as AdminProviderOverride
  const managedAccessMode = String(entry.managedAccessMode ?? 'entitlements') as ManagedAccessMode
  return {
    maintenanceMode: entry.maintenanceMode === true,
    registrationOpen: entry.registrationOpen !== false,
    providerOverride: ALLOWED_PROVIDERS.has(providerOverride) ? providerOverride : 'auto',
    managedAccessMode: ALLOWED_ACCESS_MODES.has(managedAccessMode) ? managedAccessMode : 'entitlements',
    logicQuotaPerHour: Number.isFinite(logicQuota) ? Math.max(0, Math.round(logicQuota)) : 0,
    imageQuotaPerDay: Number.isFinite(imageQuota) ? Math.max(0, Math.round(imageQuota)) : 0,
    ...(Number.isFinite(entry.updatedAtMs) ? { updatedAtMs: Number(entry.updatedAtMs) } : {}),
  }
}

export function readAdminRuntimeFlags(): AdminFeatureFlags {
  try {
    const raw = localStorage.getItem(LOCAL_FLAGS_KEY)
    return normalizeFeatureFlags(raw ? JSON.parse(raw) : {})
  } catch {
    return { ...DEFAULT_ADMIN_FEATURE_FLAGS }
  }
}

export function writeAdminRuntimeFlags(flags: AdminFeatureFlags): void {
  try {
    localStorage.setItem(LOCAL_FLAGS_KEY, JSON.stringify({
      ...normalizeFeatureFlags(flags),
      updatedAtMs: Date.now(),
    }))
  } catch {
    // Non-fatal local persistence failure.
  }
}

export function normalizeRoleGrant(raw: unknown): AdminRoleGrant | null {
  const entry = safeObject(raw)
  const uid = normalizeAdminUid(entry.uid)
  const email = normalizeAdminEmail(entry.email)
  if (!uid && !email) return null
  const role = normalizeAdminRole(entry.role, email)
  const updatedAtMs = Number(entry.updatedAtMs)
  return {
    ...(uid ? { uid } : {}),
    ...(email ? { email } : {}),
    ...(String(entry.displayName ?? '').trim() ? { displayName: String(entry.displayName).trim() } : {}),
    role,
    ...(Number.isFinite(updatedAtMs) ? { updatedAtMs: updatedAtMs } : {}),
    ...(normalizeAdminUid(entry.updatedByUid) ? { updatedByUid: normalizeAdminUid(entry.updatedByUid) } : {}),
    ...(normalizeAdminEmail(entry.updatedByEmail) ? { updatedByEmail: normalizeAdminEmail(entry.updatedByEmail) } : {}),
  }
}

export function roleGrantKey(grant: Pick<AdminRoleGrant, 'uid' | 'email'>): string {
  const email = normalizeAdminEmail(grant.email)
  if (email) return `email:${email}`
  const uid = normalizeAdminUid(grant.uid)
  return uid ? `uid:${uid}` : ''
}

export function normalizeRoleGrantList(raw: unknown): AdminRoleGrant[] {
  const source = Array.isArray(raw) ? raw : []
  const merged = new Map<string, AdminRoleGrant>()
  for (const candidate of source) {
    const grant = normalizeRoleGrant(candidate)
    if (!grant) continue
    const key = roleGrantKey(grant)
    if (!key) continue
    merged.set(key, grant)
  }
  return [...merged.values()].sort((a, b) => {
    const roleDelta = ROLE_RANK[b.role] - ROLE_RANK[a.role]
    if (roleDelta) return roleDelta
    return roleGrantKey(a).localeCompare(roleGrantKey(b))
  })
}

export function buildLegacyAdminRoleGrants(uids: unknown): AdminRoleGrant[] {
  const source = Array.isArray(uids) ? uids : []
  return normalizeRoleGrantList(source.map(uid => ({ uid, role: 'admin' })))
}

export function resolveAssignedRole(
  identity: { uid?: unknown; email?: unknown },
  grants: AdminRoleGrant[],
  options: { legacyAdmin?: boolean; e2eAdmin?: boolean } = {},
): AdminUserRole {
  const uid = normalizeAdminUid(identity.uid)
  const email = normalizeAdminEmail(identity.email)
  let resolved: AdminUserRole = email === SUPREME_ADMIN_EMAIL ? 'supreme' : 'normal'
  for (const grant of grants) {
    const emailMatch = email && normalizeAdminEmail(grant.email) === email
    const uidMatch = uid && normalizeAdminUid(grant.uid) === uid
    if (!emailMatch && !uidMatch) continue
    if (ROLE_RANK[grant.role] > ROLE_RANK[resolved]) resolved = grant.role
  }
  if (options.legacyAdmin && ROLE_RANK.admin > ROLE_RANK[resolved]) resolved = 'admin'
  if (options.e2eAdmin && ROLE_RANK.admin > ROLE_RANK[resolved]) resolved = 'admin'
  return resolved
}

export function loadLocalRoleGrants(): AdminRoleGrant[] {
  try {
    const raw = localStorage.getItem(LOCAL_ROLE_GRANTS_KEY)
    return normalizeRoleGrantList(raw ? JSON.parse(raw) : [])
  } catch {
    return []
  }
}

export function saveLocalRoleGrants(grants: AdminRoleGrant[]): void {
  try {
    localStorage.setItem(LOCAL_ROLE_GRANTS_KEY, JSON.stringify(normalizeRoleGrantList(grants)))
  } catch {
    // Non-fatal local persistence failure.
  }
}

export function loadLocalLegacyAdmins(): string[] {
  try {
    const raw = localStorage.getItem(LOCAL_ADMINS_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed)
      ? parsed.map(entry => normalizeAdminUid(entry)).filter(Boolean)
      : []
  } catch {
    return []
  }
}

export function hasE2EAdminOverride(): boolean {
  try {
    return sessionStorage.getItem(E2E_ADMIN_KEY) === '1'
  } catch {
    return false
  }
}
